import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

// 🔐 Admin pages
import AdminRegister from './pages/AdminRegister';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminAccountView from './pages/AdminAccountView';
import AdminChangePassword from './pages/AdminChangePassword';
import AdminForgotPassword from './pages/AdminForgotPassword';
import ResetPassword from './pages/ResetPassword';

// 🧩 Shared components
import Header from './components/Header';
import AccessibilityBar from './components/AccessibilityBar';
import Footer from './components/Footer';
import JobCard from './components/JobCard';
import JobCardCarousel from './components/JobCardCarousel';
import Loader from './components/Loader';

// 📄 Job-related pages
import JobDetails from './pages/JobDetails';
import GovernmentJobs from './pages/GovernmentJobs';
import PSUJobs from './pages/PSUJobs';
import RailwayJobs from './pages/RailwayJobs';
import DefenceJobs from './pages/DefenceJobs';
import BankingJobs from './pages/BankingJobs';
import TeachingJobs from './pages/TeachingJobs';
import Tools from './pages/Tools';
import ImageResizer from './pages/tools/ImageResizer';

import { API_BASE_URL } from './config/apiConfig';
import './styles/globalStyles.css';
import './App.css';

// ✅ ProtectedRoute: restrict access to admin-only pages
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('adminToken');
  const expiry = localStorage.getItem('adminTokenExpiry');
  const isExpired = expiry && Date.now() > Number(expiry);

  if (!token || isExpired) {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenExpiry');
    return <Navigate to="/admin/login" replace />;
  }

  return children;
}

function App() {
  // 🔄 Job listing state
  const [jobs, setJobs] = useState([]); // Paginated jobs
  const [jobsFilter, setJobsFilter] = useState([]); // Full DB jobs for search/carousel
  const [search, setSearch] = useState('');
  const [token, setToken] = useState(localStorage.getItem('adminToken') || null);
  const [page, setPage] = useState(1); // General pagination
  const [searchPage, setSearchPage] = useState(1); // Search pagination
  const [totalPages, setTotalPages] = useState(1);
  const [refreshTrigger, setRefreshTrigger] = useState(Date.now());
  const [jobsPerPage, setJobsPerPage] = useState(10);
  const [searchJobsPerPage] = useState(10); // Fixed for search
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);

  // 📡 Fetch paginated jobs (default view)
  useEffect(() => {
    setLoading(true);
    const url = showAll
      ? `${API_BASE_URL}/api/jobs?limit=1000&cb=${refreshTrigger}`
      : `${API_BASE_URL}/api/jobs?page=${page}&limit=${jobsPerPage}&cb=${refreshTrigger}`;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        setJobs(data.jobs || []);
        setTotalPages(showAll ? 1 : data.totalPages || 1);
        if (showAll) {
          setJobsPerPage(data.jobs?.length || 1000);
        }
        setLoading(false);
      })
      .catch(() => {
        setJobs([]);
        setLoading(false);
      });
  }, [page, refreshTrigger, jobsPerPage, showAll]);

  // 📡 Fetch full job list for search and carousel
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/jobs?limit=1000&cb=${Date.now()}`)
      .then(res => res.json())
      .then(data => {
        setJobsFilter(data.jobs || []);
      })
      .catch(() => setJobsFilter([]));
  }, []);

  // 🔍 Filter jobs from full DB based on search input
  const filteredJobs = jobsFilter.filter(job =>
    job.title?.toLowerCase().includes(search.toLowerCase()) ||
    job.department?.toLowerCase().includes(search.toLowerCase()) ||
    job.location?.toLowerCase().includes(search.toLowerCase())
  );

  // 🔁 Reset search page when search input changes
  useEffect(() => {
    setSearchPage(1);
  }, [search]);

  // 📊 Paginate search results manually
  const totalSearchPages = Math.ceil(filteredJobs.length / searchJobsPerPage);
  const paginatedSearchJobs = filteredJobs.slice(
    (searchPage - 1) * searchJobsPerPage,
    searchPage * searchJobsPerPage
  );

  // 🧱 Decide which jobs to display
  const jobsToDisplay = search.trim() ? paginatedSearchJobs : jobs;

  // 🔁 Refresh job list after posting
  const handlePostSuccess = () => {
    setRefreshTrigger(Date.now());
  };

  // 🚪 Admin logout
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setToken(null);
  };

  return (
    <>
      <Router>
        <Header token={token} onLogout={handleLogout} />
        <AccessibilityBar />

        <Routes>
          {/* 🏠 Home page: general job listing with search and pagination */}
          <Route
            path="/"
            element={
              <div className="container">
                {/* ⚠️ Carousel for Expiring Soon jobs */}
                <>
                  <h2 style={{ marginBottom: '0.5rem', marginTop: '1rem', textAlign: 'center' }}>
                    ⏳ Apply Fast!
                  </h2>
                  <JobCardCarousel jobs={jobsFilter} />
                </>

                <h1>📋 Government, PSU, Railways, Banking Jobs for you</h1>

                {/* 🔍 Search bar */}
                <input
                  className="search-bar"
                  type="text"
                  placeholder="Search by title, department, or location"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />

                {/* 🧱 Job grid */}
                <div className="grid">
                  {loading ? (
                    <Loader />
                  ) : jobsToDisplay.length > 0 ? (
                    jobsToDisplay.map(job => (
                      <JobCard key={job._id} job={job} />
                    ))
                  ) : (
                    <p>No jobs found.</p>
                  )}
                </div>

                {/* 🔁 Pagination controls — switch based on search */}
                {search.trim() ? (
                  filteredJobs.length > 0 && (
                    <div className="pagination">
                      <button disabled={searchPage === 1} onClick={() => setSearchPage(searchPage - 1)}>⬅️ Prev</button>
                      <span>Page {searchPage} of {totalSearchPages}</span>
                      <button disabled={searchPage === totalSearchPages} onClick={() => setSearchPage(searchPage + 1)}>Next ➡️</button>
                    </div>
                  )
                ) : (
                  !showAll && jobs.length > 0 && (
                    <div className="pagination">
                      <button disabled={page === 1} onClick={() => setPage(page - 1)}>⬅️ Prev</button>
                      <span>Page {page} of {totalPages}</span>
                      <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next ➡️</button>
                    </div>
                  )
                )}

                {/* 📊 Jobs per page and show all toggle */}
                <div className="jobs-per-page-control">
                  <label>
                    Jobs per page:
                    <select
                      value={jobsPerPage}
                      onChange={(e) => {
                        setJobsPerPage(Number(e.target.value));
                        setPage(1);
                        setShowAll(false);
                      }}
                      style={{ marginLeft: '0.5rem' }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </select>
                  </label>

                  <label style={{ marginLeft: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={showAll}
                      onChange={(e) => {
                        setShowAll(e.target.checked);
                        setPage(1);
                      }}
                    />
                    Show all
                  </label>
                </div>

                {/* ✅ Show all jobs count */}
                {showAll && !search.trim() && (
                  <p style={{ marginBottom: '1rem' }}>
                    ✅ Showing all jobs ({jobs.length})
                  </p>
                )}
              </div>
            }
          />

          {/* 🧰 Tools page for student utilities */}
          <Route path="/tools" element={<Tools />} />
          <Route path="/tools/image-resizer" element={<ImageResizer />} />


          {/* 📄 Job details page */}
          <Route path="/jobs/:id" element={<JobDetails />} />

          
          {/* 📂 Dedicated job type pages */}
          <Route path="/jobs/government" element={<GovernmentJobs />} />
          <Route path="/jobs/psu" element={<PSUJobs />} />
          <Route path="/jobs/railways" element={<RailwayJobs />} />
          <Route path="/jobs/defence" element={<DefenceJobs />} />
          <Route path="/jobs/banking" element={<BankingJobs />} />
          <Route path="/jobs/teaching" element={<TeachingJobs />} />

          {/* 🔐 Admin routes */}
          <Route path="/admin/register" element={<AdminRegister />} />
          <Route path="/admin/login" element={<AdminLogin setToken={setToken} />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard token={token} onPostSuccess={handlePostSuccess} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/account"
            element={
              <ProtectedRoute>
                <AdminAccountView token={token} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/change-password"
            element={
              <ProtectedRoute>
                <AdminChangePassword token={token} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/forgot-password"
            element={<AdminForgotPassword token={token} />}
          />
          <Route
            path="/admin/reset-password/:token"
            element={<ResetPassword />}
          />
        </Routes>
        <Footer />
      </Router>
    </>
  );
}

export default App;
